import * as React from 'react'
import { Slot } from '@radix-ui/react-slot'
import { cva, type VariantProps } from 'class-variance-authority'
import { cn } from '@/lib/utils'

const buttonVariants = cva(
  // Glass-/Neon-Basis: weiche Übergänge, sichtbarer Keyboard-Focus, dezenter Lift.
  'relative inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-md text-sm font-medium ring-offset-background transition-all focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50 [&_svg]:pointer-events-none [&_svg]:size-4 [&_svg]:shrink-0',
  {
    variants: {
      variant: {
        // Primary: Glass-Neon mit Theme-getuntem --primary (Kontrast je Stufe gesichert)
        default:
          'border border-[hsl(var(--primary)/0.55)] bg-[hsl(var(--primary))] text-[hsl(var(--primary-foreground))] shadow-[0_4px_16px_hsl(var(--primary)/0.35),inset_0_1px_0_rgba(255,255,255,0.25)] hover:-translate-y-0.5 hover:shadow-[0_8px_28px_hsl(var(--primary)/0.55),inset_0_1px_0_rgba(255,255,255,0.30)] active:translate-y-0',
        // Danger: Funktionsfarbe bleibt, optisch ins Glass-System integriert
        destructive:
          'border border-[hsl(var(--destructive)/0.55)] bg-[hsl(var(--destructive))] text-[hsl(var(--destructive-foreground))] shadow-[0_4px_16px_hsl(var(--destructive)/0.35),inset_0_1px_0_rgba(255,255,255,0.18)] hover:-translate-y-0.5 hover:shadow-[0_8px_26px_hsl(var(--destructive)/0.50)] active:translate-y-0',
        // Sekundär/Outline: Glass-Fläche mit Cyan-Neon-Rand
        outline:
          'border border-[rgba(var(--c2-rgb),0.40)] bg-[linear-gradient(135deg,rgba(var(--c2-rgb),0.12),rgba(var(--c2-rgb),0.05))] text-[hsl(var(--foreground))] backdrop-blur-sm shadow-[inset_0_1px_0_rgba(255,255,255,0.12)] hover:border-[rgba(var(--c2-rgb),0.62)] hover:bg-[linear-gradient(135deg,rgba(var(--c2-rgb),0.20),rgba(var(--c2-rgb),0.09))] hover:-translate-y-0.5 active:translate-y-0',
        secondary:
          'border border-[hsl(var(--border))] bg-[hsl(var(--secondary))] text-[hsl(var(--secondary-foreground))] shadow-[inset_0_1px_0_rgba(255,255,255,0.08)] hover:-translate-y-0.5 hover:bg-[hsl(var(--secondary)/0.85)] active:translate-y-0',
        ghost: 'text-[hsl(var(--foreground))] hover:bg-[rgba(var(--c2-rgb),0.10)]',
        link: 'text-primary underline-offset-4 hover:underline',
      },
      size: {
        default: 'h-10 px-4 py-2',
        sm: 'h-9 rounded-md px-3',
        lg: 'h-11 rounded-md px-8',
        icon: 'h-10 w-10',
      },
    },
    defaultVariants: {
      variant: 'default',
      size: 'default',
    },
  }
)

export interface ButtonProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement>,
    VariantProps<typeof buttonVariants> {
  asChild?: boolean
}

const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  ({ className, variant, size, asChild = false, ...props }, ref) => {
    const Comp = asChild ? Slot : 'button'
    return (
      <Comp
        className={cn(buttonVariants({ variant, size, className }))}
        ref={ref}
        {...props}
      />
    )
  }
)
Button.displayName = 'Button'

export { Button, buttonVariants }
